package dev.lingvo.site.pages.app.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import dev.lingvo.shared.BillingOverviewDto
import dev.lingvo.shared.PlanStatus
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.components.billing.BillingPlanOverview
import dev.lingvo.site.components.billing.PortalButton
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LArrowUpDownIcon
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCircleXIcon
import dev.lingvo.site.ds.LCreditCardIcon
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableAlign
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableHeaderScope
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.pages.app.projects.slug.settings.ProjectSettingsSurface
import dev.lingvo.site.state.WorkspaceActionClaim
import dev.lingvo.site.state.WorkspaceRequestContext
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.dom.Text

/**
 * What this workspace is on, and what it is paying.
 *
 * Four numbers in the order someone panics about them: status, seats, credits, renewal. Everything
 * comes from our own database — the page a customer opens when they are worried about money must not
 * depend on the payment processor answering, and that is exactly when it might not.
 *
 * A Free workspace shows the **same four numbers**: Free, one seat, 250 credits, no expiry. Free is a
 * plan, not an absence, and an empty state here would read as "billing is broken".
 */
@Page("/app/settings/billing")
@Layout(".components.layouts.AppLayout")
@Composable
fun WorkspaceBillingPage() {
    val workspaceContext = WorkspaceRequestContext.current()
    val workspace = workspaceContext?.workspace
    var refreshKey by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf(0) }

    ProjectSettingsSurface {
        Column(Modifier.fillMaxWidth().gap(Space.XXL)) {
            PageHeader(
                t("app.settings.billing.billing", "Billing"),
                tf(
                    "app.settings.billing.what_workspace_is_on",
                    "What {workspace} is on, and what it costs.",
                    "workspace" to (workspace?.name ?: t("app.settings.billing.this_workspace", "this workspace")),
                ),
                // Every other header in this lane is marked — gear, key, users, coins, scroll, bot,
                // person — and Billing's own pill in the row above carries this exact glyph, so the
                // one page about money was the only unmarked page in the group.
                mark = { LCreditCardIcon() },
                // A navigation control, drawn as one. It was a bare `Link` under the subtitle,
                // which Silk paints in its own default blue: a second accent, in no palette, on the
                // page a customer opens when they are worried about money.
                actions = {
                    LLinkButton(
                        t("app.settings.billing.compare_plans", "Compare plans"),
                        "/app/settings/billing/plans",
                        variant = LButtonVariant.SECONDARY,
                        leadingIcon = { LArrowUpDownIcon() },
                    )
                },
            )
            BillingProtectedReadLeaf(
                context = workspaceContext,
                refreshKey = refreshKey,
                billing = ApiClient::workspaceBilling,
                invoices = ApiClient::workspaceInvoices,
            ) { ownerClaim, billingReload, invoiceReload ->
                val state = billingReload.state
                val invoices = invoiceReload.state
                // Beside the numbers that are still on screen. A refresh that fails after a plan
                // change used to leave the pre-change figures up with nothing saying so.
                (billingReload.refreshError ?: invoiceReload.refreshError)?.let { failure ->
                    LAlert(
                        t("app.settings.billing.showing_the_last_answer_that_loaded", "Showing the last answer that loaded"),
                        failure.readable(),
                        variant = LAlertVariant.WARNING,
                        dense = true,
                        live = true,
                    )
                }

                LAsyncContent(
                    state,
                    rows = 4,
                    loadingLabel = t("app.settings.billing.loading_billing", "Loading billing"),
                    subject = t("app.settings.billing.billing_2", "billing"),
                    // A failed read cannot establish the workspace's payment outcome.
                    faultMessage = t("app.settings.billing.plan_status_unavailable", "We can't read your plan right now. Check billing again for the latest plan and payment status."),
                    onRetry = { refreshKey++ },
                    gap = Space.XXL,
                ) { data ->
                    // **The one-word "Cancel" was the problem, not the placement.** Drawn as a bare
                    // link beside "Compare plans", in the same user-agent blue and at the same weight,
                    // the control that ends a paying subscription was indistinguishable from the one
                    // that lists the plans — and "Cancel" beside a navigation link reads as "go back".
                    // It names what it ends now, carries a glyph, and is the quietest control on the
                    // page rather than a peer of the header's action. Ownership and a loaded subscription
                    // are both required; a paused subscription can be on Free and still have its link.
                    if (ownerClaim != null && data.hasSubscription) {
                        Row(Modifier.gap(Space.MD).flexWrap(FlexWrap.Wrap)) {
                            LLinkButton(
                                t("app.settings.billing.cancel_this_plan", "Cancel this plan"),
                                "/app/settings/billing/cancel",
                                variant = LButtonVariant.GHOST,
                                leadingIcon = { LCircleXIcon() },
                            )
                        }
                    }
                    BillingAlert(data, ownerClaim)
                    BillingPlanOverview(data, creditSummaryLine(data))
                    InvoiceTable(invoices, onRetry = { refreshKey++ })
                }
            }
        }
    }
}

/**
 * Above everything, and only when something is actually wrong.
 *
 * **Ordered candidates rather than an early return, because the early return hid the one alert that
 * carries an action.** A pending checkout used to `return` before the `when (data.status)` was
 * reached, which sounds harmless until you follow the sequence it breaks: a customer's card expires,
 * the workspace goes PAST_DUE, they click through to pay again, a `billing_checkouts` row is written
 * — and from that moment the only alert on the page is "we're confirming your payment", while the
 * PAST_DUE alert *and its `Update payment method` button* are unreachable. If that checkout does not
 * settle, the customer is looking at a reassuring sentence with no way to fix the dead card that put
 * them there.
 *
 * Priority is by what the reader can do about it, worst first. Two at most: a third stacks a column
 * of banners above the page's own content, and the third-most-urgent thing is never urgent.
 */
@Composable
private fun BillingAlert(data: BillingOverviewDto, ownerClaim: WorkspaceActionClaim?) {
    val canUseOwnerBilling = ownerClaim != null
    val candidates = buildList<@Composable () -> Unit> {
        if (data.status == PlanStatus.PAST_DUE) add { PastDueAlert(data, ownerClaim) }
        data.pendingCheckoutStartedAt?.let {
            add {
                LAlert(
                    t("app.settings.billing.checkout_status_unconfirmed", "Checkout status is unconfirmed"),
                    t("app.settings.billing.pending_checkout_not_payment", "A checkout was started for this workspace. Payment status is not confirmed. Do not pay again before checking the existing checkout."),
                    variant = LAlertVariant.INFO,
                )
            }
        }
        if (data.status == PlanStatus.CANCELING) {
            add {
                LAlert(
                    // `{date}` rather than Kotlin interpolation, for the reason `tPlural` uses
                    // `{count}`: a translation has to be able to put the date where its own grammar
                    // wants it, which a sentence assembled around a fixed prefix cannot do.
                    tf(
                        "app.settings.billing.this_plan_ends_on_date",
                        "This plan ends on {date}",
                        "date" to (
                            data.renewsAt?.take(10)
                                ?: t("app.settings.billing.the_end_of_the_period", "the end of the period")
                        ),
                    ),
                    t("app.settings.billing.nothing_changes_until_then_your_work", "Nothing changes until then. Your work stays; the plan drops to Free."),
                    variant = LAlertVariant.WARNING,
                )
            }
        }
        if (data.status == PlanStatus.PAUSED) {
            add {
                LAlert(
                    t("app.settings.billing.this_subscription_is_paused", "This subscription is paused"),
                    t("app.settings.billing.billing_has_stopped_and_paid_features", "Billing has stopped and paid features are unavailable until it resumes."),
                    variant = LAlertVariant.WARNING,
                )
            }
        }
    }
    candidates.take(2).forEach { it() }
}

@Composable
private fun PastDueAlert(data: BillingOverviewDto, ownerClaim: WorkspaceActionClaim?) {
    val canUseOwnerBilling = ownerClaim != null
    LAlert(
        t("app.settings.billing.a_payment_failed", "A payment failed"),
        // Assembled from whole sentences, each its own key. A paragraph built by appending
        // fragments cannot be translated at all — the translator receives four pieces, no way to
        // say how they join, and no way to reorder them for a language that puts the condition
        // first. Independent sentences in sequence survive that; clauses do not.
        buildString {
            append(t("app.settings.billing.your_card_was_declined", "Your card was declined."))
            // Only what the processor told us. A retry date we calculated would be a guess
            // presented as a fact, on the screen where trust is decided.
            data.dunning?.nextRetryAt?.let {
                append(' ')
                append(
                    tf(
                        "app.settings.billing.the_next_attempt_is_date",
                        "The next attempt is {date}.",
                        "date" to it.take(10),
                    ),
                )
            }
            data.dunning?.endsAt?.let {
                append(' ')
                append(
                    tf(
                        "app.settings.billing.without_a_working_card_the_plan_ends",
                        "Without a working card the plan ends {date}.",
                        "date" to it.take(10),
                    ),
                )
            }
            append(' ')
            append(
                if (canUseOwnerBilling) {
                    t(
                        "app.settings.billing.use_the_button_below_to_update",
                        "Use the button below to update it — the processor handles the card " +
                            "details, and Lingvo never sees them.",
                    )
                } else {
                    t(
                        "app.settings.billing.only_the_workspace_owner_can_update",
                        "Only the workspace owner can update the payment method. Ask them to open Billing.",
                    )
                },
            )
        },
        variant = LAlertVariant.DANGER,
        action = if (canUseOwnerBilling) {
            {
                PortalButton(
                    ownerClaim,
                    t("app.settings.billing.update_payment_method", "Update payment method"),
                )
            }
        } else {
            null
        },
    )
}

/**
 * The charges, and — when there are none to show — why.
 *
 * **A failed read used to be indistinguishable from an empty one.** `(invoices as? Async.Success)
 * ?.value.orEmpty()` followed by an early return meant a dropped connection rendered exactly what a
 * brand-new Free workspace renders: nothing at all, with no trace that a table had been intended.
 * On the page somebody opens because they are unsure what they have been charged, "we could not
 * reach the server" and "you have never been charged" are the two answers that must never be
 * confused — one of them invites a second payment.
 *
 * Genuine emptiness stays silent on purpose. `/app/settings/billing/invoices` carries the three-way
 * empty state that distinguishes Free from a settling checkout, and a Free workspace does not need
 * this page to announce a table it has no rows for.
 */
@Composable
private fun InvoiceTable(
    invoices: Async<List<dev.lingvo.shared.BillingDocumentDto>>,
    onRetry: () -> Unit,
) {
    LAsyncContent(
        invoices,
        rows = 3,
        loadingLabel = t("app.settings.billing.loading_invoices", "Loading invoices"),
        subject = t("app.settings.billing.invoices_2", "invoices"),
        faultMessage = t("app.settings.billing.invoice_status_unavailable", "We can't list your invoices right now. Check again to confirm the recorded billing documents."),
        onRetry = onRetry,
    ) { documents ->
        if (documents.isEmpty()) return@LAsyncContent
        LTable(caption = t("app.settings.billing.invoices", "Invoices")) {
            LTableHead {
                LTableRow {
                    // Spelled out rather than looped over a `listOf`: a literal inside a list is
                    // invisible to the catalogue extractor, so these four headers were the only
                    // untranslated text left on the page.
                    LTableHeaderCell { Text(t("app.settings.billing.invoice", "Invoice")) }
                    LTableHeaderCell { Text(t("app.settings.billing.date", "Date")) }
                    LTableHeaderCell { Text(t("app.settings.billing.amount", "Amount")) }
                    LTableHeaderCell { Text(t("app.settings.billing.status", "Status")) }
                }
            }
            LTableBody {
                documents.forEach { document ->
                    LTableRow {
                        LTableHeaderCell(scope = LTableHeaderScope.ROW) {
                            Text(document.invoiceNumber ?: "—")
                        }
                        LTableCell { Text(document.billedAt?.take(10) ?: "—") }
                        // The currency actually charged, formatted by the server. A locally
                        // rendered "$" on a euro invoice is a number nobody can reconcile with
                        // a bank statement.
                        LTableCell(align = LTableAlign.END) { Text(document.amount.display) }
                        LTableCell {
                            LBadge(
                                // Only our own word is translatable; `document.status` is the
                                // processor's, and inventing a key per value it might send
                                // would put strings in the catalogue nobody can verify.
                                if (document.isRefund) {
                                    t("app.settings.billing.refund", "refund")
                                } else {
                                    document.status
                                },
                                variant = if (document.isRefund) {
                                    LBadgeVariant.WARNING
                                } else {
                                    LBadgeVariant.NEUTRAL
                                },
                            )
                        }
                    }
                }
            }
        }
    }
}

/**
 * The sentence under the credit bar.
 *
 * Extracted because it is the line on this page that states a customer's money position in words,
 * and it is the line that was wrong: it called granted credits "purchased" and told the reader they
 * expire differently from the plan's. Since grants became month-scoped they are the same kind of
 * credit with a different origin — both are for this month and both reset on the 1st.
 */
internal fun creditSummaryLine(data: BillingOverviewDto): String = if (data.creditsGranted > 0) {
    trf(
        "app.settings.billing.value.summary_granted",
        "{used} of {included} included plus {granted} granted · {remaining} left",
        "used" to data.creditsUsed.toString(),
        "included" to data.creditsIncluded.toString(),
        "granted" to data.creditsGranted.toString(),
        "remaining" to data.creditsRemaining.toString(),
    )
} else {
    trf(
        "app.settings.billing.value.summary",
        "{used} of {included} included · {remaining} left",
        "used" to data.creditsUsed.toString(),
        "included" to data.creditsIncluded.toString(),
        "remaining" to data.creditsRemaining.toString(),
    )
}
